import { useQuery } from "@tanstack/react-query";

/**
 * Pembungkus tipis di atas React Query `useQuery`, mengembalikan bentuk
 * `{ data, loading, error, refetch }` — SAMA dengan pola
 * `useState` + `useEffect` manual yang dipakai puluhan halaman sekarang:
 *
 *   // pola lama
 *   const [data, setData] = useState(null);
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState(null);
 *   useEffect(() => {
 *     setLoading(true);
 *     api.get("/produk").then(r => setData(r.data)).catch(setError).finally(() => setLoading(false));
 *   }, []);
 *
 *   // pola baru, bentuk hasil identik
 *   const { data, loading, error, refetch } = useDataQuery(
 *     ["produk"],
 *     () => api.get("/produk").then(r => r.data),
 *   );
 *
 * TUJUAN: migrasi ke caching React Query jadi penggantian kecil per halaman
 * (tinggal tukar hook, bentuk variabel hasil tidak berubah), bukan perombakan
 * total. `QueryClientProvider` (staleTime 60 detik, refetchOnWindowFocus
 * false) sudah terpasang global di `src/index.js` — hook ini TIDAK menambah
 * provider baru dan TIDAK mengubah konfigurasi cache global.
 *
 * @param {Array} queryKey - key React Query, mis. ["produk", cabang, halaman].
 *   Sertakan semua variabel yang mempengaruhi hasil fetch (filter, halaman,
 *   dst) supaya cache tidak "nyangkut" menampilkan hasil filter lama.
 * @param {Function} queryFn - async () => data, biasanya `api.get(url).then(r => r.data)`.
 *   Error axios sudah ditangani interceptor global (`lib/api.js` — retry GET
 *   1x, toast Indonesia otomatis) — TIDAK perlu menangani ulang di sini.
 * @param {Object} [opsi] - opsi tambahan diteruskan langsung ke `useQuery`
 *   (mis. `enabled`, `staleTime`, `initialData`, `select`).
 * @returns {{ data: *, loading: boolean, fetching: boolean, error: Error|null, refetch: Function }}
 *   `loading` = fetch pertama (belum ada cache sama sekali), setara flag
 *   loading tunggal di pola lama. `fetching` tambahan opsional untuk
 *   indikator refresh halus (data lama tetap tampil selagi refetch jalan).
 */
export function useDataQuery(queryKey, queryFn, opsi = {}) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey,
    queryFn,
    ...opsi,
  });

  return {
    data,
    loading: isLoading,
    fetching: isFetching,
    error: isError ? error : null,
    refetch,
  };
}

export default useDataQuery;
